// Typed error normalization. No UI coupling on purpose - callers decide presentation.

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'canceled'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'server'
  | 'unknown';

const BRAND = '__isApiError';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly url: string | null;
  readonly data: unknown;

  constructor(
    kind: ApiErrorKind,
    message: string,
    status: number | null,
    url: string | null,
    data: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.url = url;
    this.data = data;
    // Needed when the build downlevels classes (old WebView targets break instanceof otherwise).
    Object.setPrototypeOf(this, ApiError.prototype);
    (this as unknown as Record<string, boolean>)[BRAND] = true;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return !!error && (error as Record<string, unknown>)[BRAND] === true;
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

// Strapi v3 sends `{ message: [{ messages: [{ message }] }] }` or a plain string.
// IZTeach gateways send `{ message }` or `{ error }`. Flatten both to one line.
function extractMessage(data: unknown, fallback: string): string {
  if (typeof data === 'string' && data) return data;
  if (!data || typeof data !== 'object') return fallback;

  const body = data as Record<string, unknown>;
  const message = body.message;

  if (typeof message === 'string' && message) return message;
  if (Array.isArray(message)) {
    const first = message[0] as Record<string, unknown> | undefined;
    const inner = first && (first.messages as Array<Record<string, unknown>> | undefined);
    const innerFirst = inner && inner[0];
    if (innerFirst && typeof innerFirst.message === 'string') return innerFirst.message;
  }
  if (typeof body.error === 'string' && body.error) return body.error;
  return fallback;
}

// What `message` holds when the response body said nothing usable - see normalizeError.
function statusFallback(status: number): string {
  return `Request failed with status ${status}`;
}

/** True when `message` is the server's own words rather than the placeholder above. */
export function hasServerMessage(error: ApiError): boolean {
  return error.status !== null && error.message !== statusFallback(error.status);
}

export function normalizeError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  const err = (error || {}) as Record<string, any>;
  const config = (err.config || {}) as Record<string, any>;
  const url = typeof config.url === 'string' ? config.url : null;

  if (err.__CANCEL__ === true) {
    return new ApiError('canceled', 'Request canceled', null, url, null);
  }
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return new ApiError('timeout', 'Request timed out', null, url, null);
  }

  const response = err.response as Record<string, any> | undefined;
  if (!response) {
    return new ApiError('network', 'Network unreachable', null, url, null);
  }

  const status = typeof response.status === 'number' ? response.status : 0;
  const message = extractMessage(response.data, statusFallback(status));
  return new ApiError(kindFromStatus(status), message, status, url, response.data);
}
